import { z } from "zod";

export const QaFinancialAidBaselineResetRecipeIdSchema = z.literal(
  "restore_financial_aid_application",
);

export const QaFinancialAidBaselineStatusSchema = z.enum([
  "invited",
  "draft",
  "submitted",
]);

export const QaFinancialAidBaselineResetPreviewInputSchema = z.object({
  applicationId: z.string().uuid(),
  tenantId: z.string().trim().min(1),
  applicantId: z.string().trim().min(1),
  familyId: z.string().trim().min(1),
  academicYear: z.string().regex(/^\d{4}-\d{4}$/),
  targetStatus: QaFinancialAidBaselineStatusSchema,
});

export const QaFinancialAidBaselineResetApplyInputSchema =
  QaFinancialAidBaselineResetPreviewInputSchema.extend({
    actorId: z.string().trim().min(1),
    expectedBeforeStateDigest: z.string().length(64),
    confirmationText: z.string().trim().min(1),
    idempotencyKey: z
      .string()
      .min(8)
      .max(160)
      .regex(/^[a-zA-Z0-9._:-]+$/),
  });

export const QaFinancialAidBaselineResetRollbackInputSchema = z.object({
  applicationId: z.string().uuid(),
  tenantId: z.string().trim().min(1),
  applicantId: z.string().trim().min(1),
  familyId: z.string().trim().min(1),
  academicYear: z.string().regex(/^\d{4}-\d{4}$/),
  targetStatus: QaFinancialAidBaselineStatusSchema,
  actorId: z.string().trim().min(1),
  rollbackToken: z.string().uuid(),
  expectedStateDigest: z.string().length(64),
  confirmationText: z.string().trim().min(1),
});

export const QaFinancialAidBaselineResetPreviewSchema = z.object({
  recipeId: QaFinancialAidBaselineResetRecipeIdSchema,
  applicationId: z.string().trim().min(1),
  applicantId: z.string().trim().min(1),
  familyId: z.string().trim().min(1),
  familyName: z.string().trim().min(1),
  academicYear: z.string().regex(/^\d{4}-\d{4}$/),
  currentStatus: z.string().trim().min(1),
  targetStatus: QaFinancialAidBaselineStatusSchema,
  affectedRecordCount: z.number().int().nonnegative(),
  preservedDocumentCount: z.number().int().nonnegative(),
  canApply: z.boolean(),
  beforeStateDigest: z.string().length(64),
  targetStateDigest: z.string().length(64),
  confirmationText: z.string().trim().min(1),
});

export const QaFinancialAidBaselineResetApplyResultSchema = z.object({
  status: z.enum(["applied", "already_applied", "no_change"]),
  preview: QaFinancialAidBaselineResetPreviewSchema,
  applicationId: z.string().trim().min(1),
  verifiedStatus: QaFinancialAidBaselineStatusSchema,
  afterStateDigest: z.string().length(64),
  rollbackToken: z.string().uuid().nullable(),
  rollbackConfirmationText: z.string().trim().min(1).nullable(),
  idempotencyKey: z.string().trim().min(1),
  appliedAt: z.string().datetime(),
});

export const QaFinancialAidBaselineResetRollbackResultSchema = z.object({
  status: z.enum(["rolled_back", "already_rolled_back"]),
  applicationId: z.string().trim().min(1),
  restoredStatus: z.string().trim().min(1),
  afterStateDigest: z.string().length(64),
  rolledBackAt: z.string().datetime(),
});

export const buildQaFinancialAidBaselineResetRollbackConfirmationText =
  (input: { familyName: string; academicYear: string }): string =>
    `ROLL BACK FINANCIAL AID FOR ${input.familyName.trim().toUpperCase()} — ${input.academicYear}`;

export type QaFinancialAidBaselineStatus = z.infer<
  typeof QaFinancialAidBaselineStatusSchema
>;
export type QaFinancialAidBaselineResetPreviewInput = z.infer<
  typeof QaFinancialAidBaselineResetPreviewInputSchema
>;
export type QaFinancialAidBaselineResetApplyInput = z.infer<
  typeof QaFinancialAidBaselineResetApplyInputSchema
>;
export type QaFinancialAidBaselineResetRollbackInput = z.infer<
  typeof QaFinancialAidBaselineResetRollbackInputSchema
>;
export type QaFinancialAidBaselineResetPreview = z.infer<
  typeof QaFinancialAidBaselineResetPreviewSchema
>;
export type QaFinancialAidBaselineResetApplyResult = z.infer<
  typeof QaFinancialAidBaselineResetApplyResultSchema
>;
export type QaFinancialAidBaselineResetRollbackResult = z.infer<
  typeof QaFinancialAidBaselineResetRollbackResultSchema
>;
